import { create } from 'zustand';

export enum OperationType {
  COPY = 'copy',
  MOVE = 'move',
  DELETE = 'delete',
  RENAME = 'rename',
  CREATE_FOLDER = 'create_folder',
  BATCH_COPY = 'batch_copy',
  BATCH_RENAME = 'batch_rename',
  BATCH_DELETE = 'batch_delete'
}

export enum OperationStatus {
  COMPLETED = 'completed',
  UNDONE = 'undone',
  REDONE = 'redone',
  FAILED = 'failed'
}

export interface OperationRecord {
  id: string;
  type: OperationType;
  timestamp: Date;
  status: OperationStatus;
  details: {
    sourcePath: string;
    targetPath?: string;
    oldName?: string;
    newName?: string;
    fileSize?: number;
    isDirectory?: boolean;
  };
  undoData?: {
    backupPath?: string;
    originalContent?: ArrayBuffer;
    metadata?: any;
  };
  children?: OperationRecord[];
}

import { webAdbService } from '@/services/webadb.service';
import { useDeviceStore } from './device.store';

interface HistoryState {
  history: OperationRecord[];
  currentIndex: number;
  isProcessing: boolean;
  undoCount: number;
  
  addRecord: (record: OperationRecord) => void;
  undo: () => Promise<void>;
  redo: () => Promise<void>;
  clearHistory: () => void;
  canUndo: () => boolean;
  canRedo: () => boolean;
}

export const useHistoryStore = create<HistoryState>((set, get) => ({
  history: [],
  currentIndex: -1,
  isProcessing: false,
  undoCount: 0,

  addRecord: (record) => set((state) => {
    const history = state.history.slice(0, state.currentIndex + 1);
    const newHistory = [...history, record];
    return {
      history: newHistory,
      currentIndex: history.length,
      undoCount: newHistory.length
    };
  }),

  undo: async () => {
    const { currentIndex, history, isProcessing } = get();
    if (currentIndex < 0 || isProcessing) return;

    const record = history[currentIndex];
    const adb = useDeviceStore.getState().adb;
    if (!adb) return;

    set({ isProcessing: true });
    try {
      switch (record.type) {
        case OperationType.RENAME:
          if (record.details.sourcePath && record.details.targetPath) {
            const isAdb = record.details.sourcePath.startsWith('adb://') || record.details.targetPath.startsWith('adb://');
            if (isAdb) {
              await webAdbService.rename(adb, record.details.targetPath.replace('adb://', ''), record.details.sourcePath.replace('adb://', ''));
            }
          }
          break;
        case OperationType.BATCH_RENAME:
          if (record.children) {
            for (const child of [...record.children].reverse()) {
              if (child.details.sourcePath && child.details.targetPath) {
                const isAdb = child.details.sourcePath.startsWith('adb://') || child.details.targetPath.startsWith('adb://');
                if (isAdb) {
                  await webAdbService.rename(adb, child.details.targetPath.replace('adb://', ''), child.details.sourcePath.replace('adb://', ''));
                }
              }
            }
          }
          break;
        case OperationType.CREATE_FOLDER:
          if (record.details.sourcePath) {
            const isAdb = record.details.sourcePath.startsWith('adb://');
            if (isAdb) {
              await webAdbService.deletePath(adb, record.details.sourcePath.replace('adb://', ''));
            }
          }
          break;
        case OperationType.COPY:
        case OperationType.BATCH_COPY:
          // Undo copy by deleting the target
          const itemsToUndo = record.type === OperationType.BATCH_COPY ? (record.children || []) : [record];
          for (const item of [...itemsToUndo].reverse()) {
            if (item.details.targetPath) {
              const isAdb = item.details.targetPath.startsWith('adb://');
              if (isAdb) {
                await webAdbService.deletePath(adb, item.details.targetPath.replace('adb://', ''));
              }
            }
          }
          break;
      }

      const updatedHistory = [...history];
      updatedHistory[currentIndex].status = OperationStatus.UNDONE;
      set({
        history: updatedHistory,
        currentIndex: currentIndex - 1,
        undoCount: currentIndex
      });
    } catch (error) {
      console.error('Undo failed:', error);
    } finally {
      set({ isProcessing: false });
    }
  },

  redo: async () => {
    const { currentIndex, history, isProcessing } = get();
    if (currentIndex >= history.length - 1 || isProcessing) return;

    const record = history[currentIndex + 1];
    const adb = useDeviceStore.getState().adb;
    if (!adb) return;

    set({ isProcessing: true });
    try {
      switch (record.type) {
        case OperationType.RENAME:
          if (record.details.sourcePath && record.details.targetPath) {
            const isAdb = record.details.sourcePath.startsWith('adb://') || record.details.targetPath.startsWith('adb://');
            if (isAdb) {
              await webAdbService.rename(adb, record.details.sourcePath.replace('adb://', ''), record.details.targetPath.replace('adb://', ''));
            }
          }
          break;
        case OperationType.BATCH_RENAME:
          if (record.children) {
            for (const child of record.children) {
              if (child.details.sourcePath && child.details.targetPath) {
                const isAdb = child.details.sourcePath.startsWith('adb://') || child.details.targetPath.startsWith('adb://');
                if (isAdb) {
                  await webAdbService.rename(adb, child.details.sourcePath.replace('adb://', ''), child.details.targetPath.replace('adb://', ''));
                }
              }
            }
          }
          break;
        case OperationType.CREATE_FOLDER:
          if (record.details.sourcePath) {
            const isAdb = record.details.sourcePath.startsWith('adb://');
            if (isAdb) {
              await webAdbService.createDirectory(adb, record.details.sourcePath.replace('adb://', ''));
            }
          }
          break;
        // Redo COPY is complex as it requires the source to still exist and re-transferring data
      }

      const updatedHistory = [...history];
      updatedHistory[currentIndex + 1].status = OperationStatus.REDONE;
      set({
        history: updatedHistory,
        currentIndex: currentIndex + 1,
        undoCount: currentIndex + 2
      });
    } catch (error) {
      console.error('Redo failed:', error);
    } finally {
      set({ isProcessing: false });
    }
  },

  clearHistory: () => set({ history: [], currentIndex: -1, undoCount: 0 }),

  canUndo: () => get().currentIndex >= 0,
  canRedo: () => get().currentIndex < get().history.length - 1,
}));
